<script lang="ts">
  import { InboxWorkflowPreconfigurationBox } from "@kontextwerk/control-web-frame"
  import {
    type InboxWorkflowJson,
    type InboxWorkflowPreconfigurationProps,
  } from "@kontextwerk/control-web-frame/inbox-workflow/contract"
  import {
    notifyInboxWorkflowPreconfigurationApplyResult,
    resolveInboxWorkflowPreconfigurationTargets,
  } from "@kontextwerk/control-web-frame"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import type { WorkOrderPrintRowState } from "../../../../_shared/_logic/print-types"
  import type { LetterSelectionDraft, LetterSelectionRow } from "../../contract"
  import {
    impersonalRecipientValue,
    manualRecipientValue,
    printRowKey,
    printStrategyForState,
    printStrategyOptions,
    rowSupportsAushang,
    setRecipientValue,
    setRowStrategyWithPropertyKlebezettelLock,
    type PrintStrategy,
  } from "../../../../_shared/_logic/print-selection"
  import {
    KEEP_PRECONFIGURATION_VALUE,
    isKeepPreconfigurationChoice,
    withKeepPreconfigurationOption,
    type PreconfigurationChoice,
  } from "../../../../../../../../../../../../_shared/_logic/preconfiguration-keep"

  type RecipientPreconfiguration = typeof manualRecipientValue | typeof impersonalRecipientValue

  let {
    rows,
    selectedIds,
    draftFor,
    patchDraft,
    disabled = false,
  }: InboxWorkflowPreconfigurationProps<LetterSelectionRow, LetterSelectionDraft> = $props()

  // Strategy and recipient start per row from print mode and contacts, so both keep by default.
  let strategy = $state<PreconfigurationChoice<PrintStrategy>>(KEEP_PRECONFIGURATION_VALUE)
  let recipient = $state<PreconfigurationChoice<RecipientPreconfiguration>>(
    KEEP_PRECONFIGURATION_VALUE,
  )

  const targetResolution = $derived(
    resolveInboxWorkflowPreconfigurationTargets({
      rows,
      selectedIds,
      getId: (row) => row.id,
      isEligible: (row) => !row.disabled && (strategy !== "NOTICE" || rowSupportsAushang(row)),
    }),
  )

  const applyToRowDrafts = () => {
    let rowState: Record<string, WorkOrderPrintRowState> = Object.fromEntries(
      rows.map((row) => [printRowKey(row), draftFor(row) as unknown as WorkOrderPrintRowState]),
    )
    for (const row of targetResolution.targetRows) {
      const rowId = printRowKey(row)

      if (!isKeepPreconfigurationChoice(strategy)) {
        rowState = setRowStrategyWithPropertyKlebezettelLock(rowState, rows, row, strategy)
      }

      if (
        !isKeepPreconfigurationChoice(recipient) &&
        printStrategyForState(rowState[rowId]) === "LETTER"
      ) {
        rowState = setRecipientValue(rowState, row, recipient)
      }

      if (rowState[rowId]) {
        rowState[rowId] = {
          ...rowState[rowId],
          personalConfigConfirmed: true,
        }
      }
    }
    for (const row of rows) {
      const next = rowState[printRowKey(row)]

      if (next) patchDraft(row, next as unknown as InboxWorkflowJson)
    }
  }

  const apply = () => {
    if (!isKeepPreconfigurationChoice(strategy) || !isKeepPreconfigurationChoice(recipient)) {
      applyToRowDrafts()
    }

    notifyInboxWorkflowPreconfigurationApplyResult({
      total: targetResolution.targetRows.length + targetResolution.skippedRows.length,
      applied: targetResolution.targetRows.length,
      skipped: targetResolution.skippedRows.length,
      reason:
        strategy === "NOTICE" ? "Aushangsstrategie ist nur für Wohneinheiten erlaubt." : undefined,
    })
  }
</script>

<InboxWorkflowPreconfigurationBox
  selectedCount={selectedIds.length}
  targetCount={targetResolution.targetRows.length}
  {disabled}
  onApply={apply}
>
  <div class="preconfiguration-grid">
    <Input
      type="select"
      label="Strategie"
      bind:value={strategy}
      brightBackground={false}
      surfaceTone="dark"
      options={withKeepPreconfigurationOption(
        printStrategyOptions
          .filter((option) => option.value)
          .map((option) => ({ value: option.value, name: option.name })),
      )}
    />
    <Input
      type="select"
      label="Empfänger"
      bind:value={recipient}
      brightBackground={false}
      surfaceTone="dark"
      disabled={strategy === "NOTICE" || strategy === "IGNORE"}
      options={withKeepPreconfigurationOption([
        { value: manualRecipientValue, name: "Individuell erfassen" },
        { value: impersonalRecipientValue, name: "Unpersönlich" },
      ])}
    />
  </div>
</InboxWorkflowPreconfigurationBox>

<style>
  .preconfiguration-grid {
    padding-top: 1rem;
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(2, minmax(12rem, 1fr));
  }
</style>
